import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { Report } from "@hydrantid/acm-client";
import { PagePermissions } from "../access-control/page-permissions/page-permissions";
import { FeatureFlags } from "../access-control/feature-flags/feature-flags";
import { AccessControlRequirements } from "../access-control/access-control";

/** Fixed identifiers for the application's navigation destinations. Dynamic report identifiers remain strings. */
export const NAVIGATION_MENU_ITEM_ID = {
    DASHBOARD: "dashboard",
    ACCOUNTS: "accounts",
    CERTIFICATES: "certificates",
    CERTIFICATES_VIEW: "certificates-view",
    CERTIFICATES_REQUEST: "certificates-request",
    CERTIFICATES_UPLOAD: "certificates-upload",
    CERTIFICATE_AUTHORITIES: "certificate-authorities",
    REQUESTS: "requests",
    DOMAINS: "domains",
    TLS_DISCOVERY: "tls-discovery",
    TLS_DISCOVERY_PROBES: "tls-discovery-probes",
    USERS: "users",
    REPORTS: "reports",
    LOGS: "logs",
    HELP: "help",
    ADMINISTRATION: "administration",
    ADMIN_ACCOUNTS: "admin-accounts",
    ADMIN_REPORTS: "admin-reports",
    PROFILE: "profile",
} as const;

export type NavigationMenuItemId =
    | (typeof NAVIGATION_MENU_ITEM_ID)[keyof typeof NAVIGATION_MENU_ITEM_ID]
    | `report-${string}`;

/** Navigation menu definitions shared by application configuration and resolved user navigation. */
export interface NavigationMenuItemBase {
    id: NavigationMenuItemId;
    label: string;
    route: readonly string[];
    icon: IconDefinition;
    /** Route matching and accessibility options for the active state. */
    activeState?: {
        exact: boolean;
        ariaCurrent: "page" | "location";
    };
}

/** Application navigation menu definitions configured before user-specific visibility is applied. */
export interface AppNavigationMenuItem extends NavigationMenuItemBase {
    /** The shared route and navigation access requirements for this destination. */
    accessControl: AccessControlRequirements;
    /** A menu-only availability condition, independent of route access. */
    visibleWhen?: (context: UserNavigationMenuContext) => boolean;
    /** Submenu destinations support no further nesting. */
    children?:
        | readonly Omit<AppNavigationMenuItem, "children">[]
        | ((context: UserNavigationMenuContext) => readonly Omit<AppNavigationMenuItem, "children">[]);
}

/** User navigation menu definitions resolved for the current logged-in user. */
export interface UserNavigationMenuItem extends Readonly<NavigationMenuItemBase> {
    readonly children: readonly (Omit<UserNavigationMenuItem, "children"> & { readonly children: readonly [] })[];
}

/** The state used to resolve the current logged-in users navigation menu. */
export interface UserNavigationMenuContext {
    pagePermissions: PagePermissions;
    featureFlags: FeatureFlags;
    reports: readonly Report[] | undefined;
}
